import React, { useState, useMemo } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Package,
  Users,
  CheckCircle,
  XCircle,
  Plane,
  Hotel,
} from "lucide-react";
import { Pagination } from "../components/common/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import PrimaryButton from "../components/ui/PrimaryButton";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import {
  useAgentAssignments,
  useCreateAgentAssignment,
  useUpdateAgentAssignment,
  useDeactivateAgentAssignment,
} from "../hooks/useAgentAssignments";
import { useAgents } from "../hooks/useAgents";
import { useCustomers } from "../hooks/useCustomers";
import { commissionService } from "../api/services/commission/commissionService";
import { useQuery } from "@tanstack/react-query";
import {
  CreateAgentAssignmentRequest,
  UpdateAgentAssignmentRequest,
  AgentAssignment,
} from "../api/services/agentAssignment/types";
import { PlayerDetailsManagement } from "../components/playerDetails/PlayerDetailsManagement";
import { PlayerFacilitiesManagement } from "../components/facilities/PlayerFacilitiesManagement";

export const AgentAssignments: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] =
    useState<AgentAssignment | null>(null);
  const [deleteAssignment, setDeleteAssignment] =
    useState<AgentAssignment | null>(null);
  const [selectedAssignmentForDetails, setSelectedAssignmentForDetails] =
    useState<AgentAssignment | null>(null);
  const [selectedAssignmentForFacilities, setSelectedAssignmentForFacilities] =
    useState<AgentAssignment | null>(null);
  const [formData, setFormData] = useState<
    CreateAgentAssignmentRequest | UpdateAgentAssignmentRequest
  >({
    agent_id: "",
    player_id: "",
    package_type_id: "",
    agent_type: "MASTER",
    commission_percentage: undefined,
    share_percentage: undefined,
    max_bet_limit: 0,
    facilities_agreed: false,
  });

  const itemsPerPage = 10;

  // Fetch data
  const { data: assignmentsData, isLoading: loadingAssignments } =
    useAgentAssignments({
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      is_active: true,
    });

  const { data: agents } = useAgents();
  const { data: customers } = useCustomers();
  const { data: packageTypes } = useQuery({
    queryKey: ["package-types"],
    queryFn: () => commissionService.getPackageTypes(),
  });

  // Mutations
  const createMutation = useCreateAgentAssignment();
  const updateMutation = useUpdateAgentAssignment();
  const deactivateMutation = useDeactivateAgentAssignment();

  // Filter assignments based on search
  const filteredAssignments = useMemo(() => {
    if (!assignmentsData?.items) return [];
    if (!searchTerm) return assignmentsData.items;

    return assignmentsData.items.filter((assignment) => {
      const agentName = assignment.agent?.name?.toLowerCase() || "";
      const playerName = assignment.player?.name?.toLowerCase() || "";
      const searchLower = searchTerm.toLowerCase();
      return (
        agentName.includes(searchLower) || playerName.includes(searchLower)
      );
    });
  }, [assignmentsData?.items, searchTerm]);

  const totalPages = useMemo(() => {
    return Math.ceil((assignmentsData?.total || 0) / itemsPerPage) || 1;
  }, [assignmentsData?.total]);

  const handleCreateOrUpdate = async () => {
    try {
      if (editingAssignment) {
        const updateData: UpdateAgentAssignmentRequest = {
          commission_percentage: formData.commission_percentage,
          share_percentage: formData.share_percentage,
          max_bet_limit: formData.max_bet_limit,
          facilities_agreed: formData.facilities_agreed,
        };
        await updateMutation.mutateAsync({
          id: editingAssignment.id,
          data: updateData,
        });
      } else {
        await createMutation.mutateAsync(
          formData as CreateAgentAssignmentRequest,
        );
      }
      setShowModal(false);
      setEditingAssignment(null);
      resetForm();
    } catch (error) {
      console.error("Error saving assignment:", error);
    }
  };

  const handleDelete = async () => {
    if (!deleteAssignment) return;
    try {
      await deactivateMutation.mutateAsync(deleteAssignment.id);
      setDeleteAssignment(null);
    } catch (error) {
      console.error("Error deactivating assignment:", error);
    }
  };

  const openEditModal = (assignment: AgentAssignment) => {
    setEditingAssignment(assignment);
    setFormData({
      commission_percentage: assignment.commission_percentage,
      share_percentage: assignment.share_percentage,
      max_bet_limit: assignment.max_bet_limit,
      facilities_agreed: assignment.facilities_agreed,
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingAssignment(null);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      agent_id: "",
      player_id: "",
      package_type_id: "",
      agent_type: "MASTER",
      commission_percentage: undefined,
      share_percentage: undefined,
      max_bet_limit: 0,
      facilities_agreed: false,
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">
            Agent-Player Assignments
          </h1>
          <p className="text-slate-400 mt-1">
            Manage assignments between agents and players with commission
            configurations
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-slate-800 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by agent or player name..."
              />
            </div>
            <PrimaryButton onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              New Assignment
            </PrimaryButton>
          </div>
        </div>

        {/* Assignments Table */}
        <div className="bg-slate-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Player
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Commission %
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Bet Limit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Facilities
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loadingAssignments ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredAssignments.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-12 text-center text-slate-400"
                  >
                    {searchTerm
                      ? "No assignments found matching your search"
                      : "No assignments created yet"}
                  </td>
                </tr>
              ) : (
                filteredAssignments.map((assignment) => (
                  <tr
                    key={assignment.id}
                    className="hover:bg-slate-700/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 text-slate-400 mr-2" />
                        <span className="text-white font-medium">
                          {assignment.agent?.name || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-slate-300">
                        {assignment.player?.name || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="w-4 h-4 text-slate-400 mr-2" />
                        <span className="text-slate-300">
                          {assignment.package_type?.name || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-900/50 text-blue-300 border border-blue-700">
                        {assignment.agent_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-slate-300">
                        {assignment.commission_percentage || 0}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-slate-300">
                        {formatCurrency(assignment.max_bet_limit)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {assignment.facilities_agreed ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-slate-500" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() =>
                            setSelectedAssignmentForDetails(assignment)
                          }
                          className="text-blue-400 hover:text-blue-300"
                          title="Travel Details"
                        >
                          <Plane className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            setSelectedAssignmentForFacilities(assignment)
                          }
                          className="text-green-400 hover:text-green-300"
                          title="Facilities"
                        >
                          <Hotel className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(assignment)}
                          className="text-blue-400 hover:text-blue-300"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteAssignment(assignment)}
                          className="text-red-400 hover:text-red-300"
                          title="Deactivate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">
                {editingAssignment
                  ? "Edit Assignment"
                  : "Create New Assignment"}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              {!editingAssignment && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Agent *
                      </label>
                      <select
                        value={
                          (formData as CreateAgentAssignmentRequest).agent_id
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            agent_id: e.target.value,
                          } as CreateAgentAssignmentRequest)
                        }
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Agent</option>
                        {agents?.items?.map(
                          (agent: { id: string; name: string }) => (
                            <option key={agent.id} value={agent.id}>
                              {agent.name}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Player *
                      </label>
                      <select
                        value={
                          (formData as CreateAgentAssignmentRequest).player_id
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            player_id: e.target.value,
                          } as CreateAgentAssignmentRequest)
                        }
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Player</option>
                        {customers?.items?.map(
                          (customer: { id: string; name: string }) => (
                            <option key={customer.id} value={customer.id}>
                              {customer.name}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Package Type *
                      </label>
                      <select
                        value={
                          (formData as CreateAgentAssignmentRequest)
                            .package_type_id
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            package_type_id: e.target.value,
                          } as CreateAgentAssignmentRequest)
                        }
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Package</option>
                        {packageTypes?.map(
                          (pkg: { id: string; name: string }) => (
                            <option key={pkg.id} value={pkg.id}>
                              {pkg.name}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Agent Type *
                      </label>
                      <select
                        value={
                          (formData as CreateAgentAssignmentRequest).agent_type
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            agent_type: e.target.value,
                          } as CreateAgentAssignmentRequest)
                        }
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="MASTER">Master</option>
                        <option value="SUPER">Super</option>
                        <option value="SUB">Sub</option>
                        <option value="JUNKET">Junket</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Commission Percentage
                  </label>
                  <input
                    type="number"
                    value={formData.commission_percentage || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        commission_percentage: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 10"
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Share Percentage
                  </label>
                  <input
                    type="number"
                    value={formData.share_percentage || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        share_percentage: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 5"
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Maximum Bet Limit
                </label>
                <input
                  type="number"
                  value={formData.max_bet_limit || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_bet_limit: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 100000"
                  min="0"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="facilities"
                  checked={formData.facilities_agreed}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      facilities_agreed: e.target.checked,
                    })
                  }
                  className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="facilities"
                  className="ml-2 text-sm font-medium text-slate-300"
                >
                  Facilities Agreed
                </label>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-slate-700">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingAssignment(null);
                }}
                className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrUpdate}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : editingAssignment
                    ? "Update"
                    : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteAssignment && (
        <ConfirmDialog
          open={true}
          onConfirm={handleDelete}
          onCancel={() => setDeleteAssignment(null)}
          title="Deactivate Assignment"
          description={`Are you sure you want to deactivate this assignment? This action can be reversed later if needed.`}
          confirmText="Deactivate"
          cancelText="Cancel"
        />
      )}

      {/* Player Details Modal */}
      {selectedAssignmentForDetails && (
        <PlayerDetailsManagement
          playerId={selectedAssignmentForDetails.player_id}
          agentId={selectedAssignmentForDetails.agent_id}
          onClose={() => setSelectedAssignmentForDetails(null)}
        />
      )}

      {/* Player Facilities Modal */}
      {selectedAssignmentForFacilities && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-white">
                  Player Facilities Management
                </h2>
                <button
                  onClick={() => setSelectedAssignmentForFacilities(null)}
                  className="text-slate-400 hover:text-slate-300"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              <p className="text-slate-400 mt-1">
                Player: {selectedAssignmentForFacilities.player?.name || "N/A"}
              </p>
            </div>

            <div className="p-6">
              <PlayerFacilitiesManagement
                playerId={selectedAssignmentForFacilities.player_id}
                assignmentId={selectedAssignmentForFacilities.id}
                canApprove={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
